import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./Dashboard.css";
import { getEmotionHomePageSummaryService, getCurrentUserRoleService } from "services/api-service";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import SearchIcon from "@mui/icons-material/Search";
import { useLocation } from "react-router-dom";

import routes from "routes.js";
import MemberAttendanceBoard from "components/member-dashboard/memberboard";
import Staffboard from "components/staff-dashboard/staffboard";
import PeopleCountingBoard from "components/people-counting/PeopleCountingBoard"; 
import ilogo from "assets/img/Clogo1.png";

function Dashboard() {
  const theme = useTheme();
  const md_matches = useMediaQuery(theme.breakpoints.down("md"));
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    getCurrentUserRoleService()
      .then(res => setUserRole(res.data.role_type))
      .catch(err => console.error("Error fetching user role:", err));
  }, []);

  // ✅ Default View set to "member"
  const [activeView, setActiveView] = useState("member"); 

  const [selectedChartData, setSelectedChartData] = useState("");
  const [emotionChart, setEmotionChart] = useState([]);

  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem("selectData") || formattedToday
  );

  useEffect(() => {
    localStorage.setItem("selectedDatesw", selectedDate);
    localStorage.setItem("seDate", selectedDate);
  }, [selectedDate]);

  const [homePageSummary, setHomePageSummary] = useState(() => {
    const storedData = localStorage.getItem("homePageSummary");
    return storedData ? JSON.parse(storedData) : [];
  });

  const fetchDataByDate = async () => {
    if (!selectedDate) {
      toast.error("Please select a valid date.");
      return;
    }

    try {
      const response = await getEmotionHomePageSummaryService({
        date: selectedDate,
      });
      setEmotionChart([]);
      setHomePageSummary(response.data);
    } catch (error) {
      toast.error("Error: Failed to fetch data for the selected date.");
      console.error("Error fetching data by date:", error);
    }
  };

  const handleStartDateChange = (e) => {
    setSelectedDate(e.target.value);
    localStorage.setItem("selectedDatett", e.target.value);
  };

  useEffect(() => {
    if (selectedDate) {
      fetchDataByDate();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedChartData) {
      let id = selectedChartData?.split(" ");
      const filter = homePageSummary.filter((item) => {
        return item.session_id == id[1];
      });
      setEmotionChart([filter[0]]);
    }
  }, [selectedChartData, homePageSummary]);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: md_matches ? "column" : "row",
          justifyContent: "space-between",
          alignItems: md_matches ? "flex-start" : "center",
          gap: "15px",
          padding: "15px 20px",
          marginBottom: "20px",
          width: "100%",
          background: "transparent",
          overflow: "visible", // ✅ FIX: Prevents tour guide clipping
        }}
      >
        {/* Logo Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          <img
            src={ilogo}
            alt="CamSec AI Pro"
            style={{
              height: md_matches ? "35px" : "40px",
              width: "auto",
              objectFit: "contain",
            }}
          />
          <span
            style={{
              fontSize: md_matches ? "14px" : "18px",
              fontWeight: "900",
              color: "#000",
              whiteSpace: "nowrap"
            }}
          >
            CamSecAI_PROMINI
          </span>
        </div>

        {/* Controls Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "15px",
            justifyContent: md_matches ? "flex-start" : "flex-end",
            flex: 1,
          }}
        >
          {/* Tabs Container - User Role Check Removed & Overflow fixed */}
          <div style={{
            display: "flex",
            background: "#1e3a5f",
            borderRadius: "8px",
            padding: "4px",
            gap: "4px",
            flexWrap: "nowrap", // ✅ FIX: Prevents tabs from breaking into multiple lines
            overflowX: "auto",  
            maxWidth: "100%",
          }}>
            <button
              type="button"
              id="tour-staff-tab" // ✅ FIX: Added Tour Guide ID
              onClick={() => setActiveView("staff")}
              style={{
                position: "relative", // ✅ FIX: Needed for tour spotlight
                zIndex: 10,
                background: activeView === "staff" ? "#2563eb" : "transparent",
                color: activeView === "staff" ? "#fff" : "#cbd5e1",
                border: "none", borderRadius: "6px", fontWeight: "700", padding: "6px 12px",
                fontSize: "13px", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap"
              }}
            >
              👥 Staff
            </button>
            <button
              type="button"
              onClick={() => setActiveView("member")}
              style={{
                background: activeView === "member" ? "#2563eb" : "transparent",
                color: activeView === "member" ? "#fff" : "#cbd5e1",
                border: "none", borderRadius: "6px", fontWeight: "700", padding: "6px 12px",
                fontSize: "13px", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap"
              }}
            >
              🌟 Customers
            </button>
            <button
              type="button"
              onClick={() => setActiveView("counting")}
              style={{
                background: activeView === "counting" ? "#2563eb" : "transparent",
                color: activeView === "counting" ? "#fff" : "#cbd5e1",
                border: "none", borderRadius: "6px", fontWeight: "700", padding: "6px 12px",
                fontSize: "13px", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap"
              }}
            >
              🚶 Counting
            </button>
          </div>

          {/* Date Picker & Search */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <span style={{ color: "rgba(0, 0, 0, 0.6)", fontWeight: "600", fontSize: "14px" }}>
              📅
            </span>
            <input
              type="date"
              className="date-input"
              value={selectedDate}
              onChange={handleStartDateChange}
              aria-label="Select date"
              style={{
                height: "38px",
                width: "150px",
                padding: "6px 10px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
            <div
              style={{ cursor: "pointer", display: "flex", alignItems: "center", color: "#000" }}
              onClick={fetchDataByDate}
            >
              <SearchIcon style={{ fontSize: "24px" }} />
            </div>
          </div>
        </div>
      </div>

      <Container fluid>
        <Row>
          <Col md="12">
            {activeView === "member" && (
              <MemberAttendanceBoard selectedDate={selectedDate} />
            )}
            {activeView === "staff" && (
              <Staffboard />
            )}
            {activeView === "counting" && (
              <PeopleCountingBoard selectedDate={selectedDate} />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Dashboard;
